import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { FilterRail } from "./components/FilterRail";
import { Header } from "./components/Header";
import { LoginGate } from "./components/LoginGate";
import { MobileNav } from "./components/MobileNav";
import { PhotoArchive } from "./components/PhotoArchive";
import { PhotoDetails } from "./components/PhotoDetails";
import { UploadSheet } from "./components/UploadSheet";
import { demoPhotos } from "./data/demoPhotos";

function matchesFilter(photo, type, value) {
  if (!value || type === "all") return true;
  if (type === "time") return String(new Date(photo.capturedAt).getFullYear()) === value;
  if (type === "location") return photo.location === value;
  if (type === "solarTerm") return photo.solarTerm === value;
  if (type === "style") return (photo.styles || []).includes(value);
  return true;
}

function headingFor(photos, type, value) {
  if (value) return value;
  if (type === "time") return "时间里的照片";
  if (type === "location") return "走过的地方";
  if (type === "solarTerm") return "二十四节气";
  if (type === "style") return "影像风格";
  const first = photos[0];
  if (!first) return "我的照片";
  return `${first.solarTerm} · ${first.location.split("·")[0].trim()}`;
}

function filterOptions(photos, type) {
  if (type === "time") return [...new Set(photos.map((photo) => String(new Date(photo.capturedAt).getFullYear())))];
  if (type === "location") return [...new Set(photos.map((photo) => photo.location).filter(Boolean))];
  if (type === "solarTerm") return [...new Set(photos.map((photo) => photo.solarTerm).filter(Boolean))];
  if (type === "style") return [...new Set(photos.flatMap((photo) => photo.styles || []))];
  return [];
}

export default function App() {
  const [config, setConfig] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterValue, setFilterValue] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    api.config()
      .then((nextConfig) => {
        setConfig(nextConfig);
        setAuthenticated(nextConfig.authenticated);
        if (nextConfig.authenticated) return loadPhotos();
        return null;
      })
      .catch((error) => setNotice(error.message))
      .finally(() => setLoading(false));
  }, []);

  async function loadPhotos() {
    const stored = await api.photos();
    setPhotos(stored.length ? stored : demoPhotos);
  }

  async function unlock(key) {
    await api.unlock(key);
    setAuthenticated(true);
    await loadPhotos();
  }

  function chooseFilterType(type) {
    setFilterType(type);
    setFilterValue("");
  }

  const visiblePhotos = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("zh-CN");
    return photos.filter((photo) => {
      const matchesQuery = !query || [photo.title, photo.location, photo.solarTerm, ...(photo.styles || [])]
        .join(" ")
        .toLocaleLowerCase("zh-CN")
        .includes(query);
      return matchesQuery && matchesFilter(photo, filterType, filterValue);
    });
  }, [photos, filterType, filterValue, search]);
  const mobileFilterOptions = useMemo(() => filterOptions(photos, filterType), [photos, filterType]);

  async function savePhoto(photo, changes) {
    if (photo.isDemo) return;
    const updated = await api.updatePhoto(photo.id, changes);
    setPhotos((current) => current.map((item) => item.id === photo.id ? updated : item));
    setSelectedPhoto(updated);
    setNotice("修改已保存");
    window.setTimeout(() => setNotice(""), 1800);
  }

  async function deletePhoto(photo) {
    if (!window.confirm(`确定从相册删除“${photo.title}”吗？原图也会被移除。`)) return;
    await api.deletePhoto(photo.id);
    setPhotos((current) => current.filter((item) => item.id !== photo.id));
    setSelectedPhoto(null);
    setNotice("照片已删除");
    window.setTimeout(() => setNotice(""), 1800);
  }

  function handleUploaded(created) {
    setPhotos((current) => [
      ...created,
      ...current.filter((photo) => !photo.isDemo && !created.some((item) => item.id === photo.id)),
    ]);
    setUploadOpen(false);
    setFilterType("all");
    setFilterValue("");
    setNotice(`${created.length} 张照片已进入相册`);
    window.setTimeout(() => setNotice(""), 2400);
  }

  if (loading || !config) {
    return <div className="loading-screen"><span>空の记忆</span><i /></div>;
  }

  if (config.requiresAuth && !authenticated) return <LoginGate onUnlock={unlock} />;

  return (
    <div className={`app-shell ${selectedPhoto ? "details-open" : ""}`}>
      <div className="seasonal-particles" aria-hidden="true">
        {Array.from({ length: 14 }, (_, index) => <i key={index} />)}
      </div>

      <Header
        filterType={filterType}
        onFilterType={chooseFilterType}
        onImport={() => setUploadOpen(true)}
        search={search}
        onSearch={setSearch}
      />

      {filterType !== "all" && (
        <div className="mobile-filter-values" aria-label="具体筛选条件">
          <button className={!filterValue ? "active" : ""} type="button" onClick={() => setFilterValue("")}>全部</button>
          {mobileFilterOptions.map((option) => (
            <button className={filterValue === option ? "active" : ""} type="button" key={option} onClick={() => setFilterValue(option)}>
              {option}
            </button>
          ))}
        </div>
      )}

      <div className="workspace">
        <FilterRail
          type={filterType}
          photos={photos}
          value={filterValue}
          onChange={(value, inferredType) => {
            if (filterType === "all") setFilterType(inferredType);
            setFilterValue(value);
          }}
        />
        <PhotoArchive
          photos={visiblePhotos}
          selectedId={selectedPhoto?.id}
          onSelect={setSelectedPhoto}
          onImport={() => setUploadOpen(true)}
          heading={headingFor(visiblePhotos.length ? visiblePhotos : photos, filterType, filterValue)}
        />
        <PhotoDetails
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          onSave={savePhoto}
          onDelete={deletePhoto}
        />
      </div>

      <MobileNav onImport={() => setUploadOpen(true)} onFilters={() => document.querySelector(".filter-tabs")?.scrollIntoView({ behavior: "smooth" })} />
      <UploadSheet open={uploadOpen} onClose={() => setUploadOpen(false)} onComplete={handleUploaded} />

      {notice && <div className="toast" role="status">{notice}</div>}
    </div>
  );
}
